import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format, subDays, parseISO, differenceInDays, startOfWeek, eachDayOfInterval } from "date-fns";
import { 
  TrendingUp, TrendingDown, Minus, AlertTriangle, CheckCircle2, 
  Activity, Brain, Heart, Moon, Target, MessageSquare, Calendar,
  BarChart3, ArrowUpRight, ArrowDownRight, Info, Scale
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, BarChart, Bar } from "recharts";
import { toast } from "sonner";
import { ClientBalanceView } from "./ClientBalanceView";

interface ClientInsightsDashboardProps {
  clientId: string;
  clinicianId: string;
  clientName: string;
  onBack?: () => void;
}

interface CheckinData {
  date: string;
  emotion: string;
  energy_field: string;
  body_feeling: string;
  mind_state: string;
  spiritual_connection: string;
  relational_feeling: string;
}

interface SleepData {
  date: string;
  duration_min: number;
  quality_self_rating: number;
}

interface GoalData {
  id: string;
  title: string;
  category: string;
  status: string;
  target_value: number;
  current_value: number;
  created_at: string;
  completed_date: string | null;
}

interface CrisisEvent {
  id: string;
  timestamp: string;
  trigger: string;
  severity_score: number;
  action_taken: string;
}

interface JournalEntry {
  id: string;
  created_at: string;
  mood: string;
  title: string;
}

// Emotion to numeric score mapping
const emotionScores: Record<string, number> = {
  'Joy': 10, 'Peace': 9, 'Love': 10, 'Gratitude': 9, 'Hope': 8, 'Content': 8, 'Calm': 7,
  'Neutral': 5, 'Mixed': 5, 'Tired': 4, 'Stressed': 3, 'Anxious': 3, 'Worried': 3,
  'Sad': 2, 'Angry': 2, 'Fear': 2, 'Overwhelmed': 2, 'Lonely': 2, 'Shame': 1, 'Despair': 1
};

// Body feeling scores
const bodyScores: Record<string, number> = {
  'Vibrant': 10, 'Energized': 9, 'Strong': 8, 'Relaxed': 7, 'Comfortable': 6,
  'Neutral': 5, 'Tired': 4, 'Tense': 3, 'Heavy': 2, 'Exhausted': 1, 'Pain': 1
};

// Mind state scores
const mindScores: Record<string, number> = {
  'Clear': 10, 'Focused': 9, 'Creative': 8, 'Calm': 7, 'Open': 7,
  'Busy': 5, 'Scattered': 4, 'Racing': 3, 'Foggy': 3, 'Overwhelmed': 2, 'Anxious': 2
};

export function ClientInsightsDashboard({ clientId, clinicianId, clientName, onBack }: ClientInsightsDashboardProps) {
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("30");
  const [checkins, setCheckins] = useState<CheckinData[]>([]);
  const [sleepData, setSleepData] = useState<SleepData[]>([]);
  const [goals, setGoals] = useState<GoalData[]>([]);
  const [crisisEvents, setCrisisEvents] = useState<CrisisEvent[]>([]);
  const [journalEntries, setJournalEntries] = useState<JournalEntry[]>([]);
  const [consent, setConsent] = useState<any>(null);

  useEffect(() => {
    loadConsent();
  }, [clientId, clinicianId]);

  useEffect(() => {
    if (consent) {
      loadAllData();
    }
  }, [consent, timeRange]);

  const loadConsent = async () => {
    const { data, error } = await supabase
      .from('client_data_consent')
      .select('*')
      .eq('client_id', clientId)
      .eq('clinician_id', clinicianId)
      .maybeSingle();

    if (error) {
      console.error('Error loading consent:', error);
    }
    setConsent(data);
    if (!data) setLoading(false);
  };

  const loadAllData = async () => {
    setLoading(true);
    const startDate = subDays(new Date(), parseInt(timeRange)).toISOString();

    // Load check-ins if consented
    if (consent?.share_check_ins || consent?.share_mood_data) {
      const { data } = await supabase
        .from('daily_checkins')
        .select('date, emotion, energy_field, body_feeling, mind_state, spiritual_connection, relational_feeling')
        .eq('user_id', clientId)
        .gte('date', startDate.split('T')[0])
        .order('date', { ascending: true });
      setCheckins(data || []);
    }

    // Load sleep data
    if (consent?.share_sleep_data) {
      const { data } = await supabase
        .from('sleep_logs')
        .select('date, duration_min, quality_self_rating')
        .eq('user_id', clientId)
        .gte('date', startDate.split('T')[0])
        .order('date', { ascending: true });
      setSleepData(data || []);
    }

    // Load goals
    if (consent?.share_goals_progress) {
      const { data } = await supabase
        .from('goals')
        .select('id, title, category, status, target_value, current_value, created_at, completed_date')
        .eq('user_id', clientId)
        .order('created_at', { ascending: false });
      setGoals(data || []);
    }

    // Load crisis events
    if (consent?.share_crisis_events) {
      const { data } = await supabase
        .from('crisis_events')
        .select('id, timestamp, trigger, severity_score, action_taken')
        .eq('user_id', clientId)
        .gte('timestamp', startDate)
        .order('timestamp', { ascending: false });
      setCrisisEvents(data || []);
    }

    // Load journal entries
    if (consent?.share_journal_entries) {
      const { data } = await supabase
        .from('journal_entries')
        .select('id, created_at, mood, title')
        .eq('user_id', clientId)
        .gte('created_at', startDate)
        .order('created_at', { ascending: false });
      setJournalEntries(data || []);
    }

    setLoading(false);
  };

  // Calculate statistics
  const stats = useMemo(() => {
    if (checkins.length === 0) return null;

    const moodScores = checkins
      .map(c => emotionScores[c.emotion] || 5)
      .filter(s => s > 0);
    
    const bodyScoresArr = checkins
      .map(c => bodyScores[c.body_feeling] || 5)
      .filter(s => s > 0);

    const mindScoresArr = checkins
      .map(c => mindScores[c.mind_state] || 5)
      .filter(s => s > 0);

    const avgMood = moodScores.length > 0 
      ? moodScores.reduce((a, b) => a + b, 0) / moodScores.length 
      : 0;

    const avgBody = bodyScoresArr.length > 0
      ? bodyScoresArr.reduce((a, b) => a + b, 0) / bodyScoresArr.length
      : 0;

    const avgMind = mindScoresArr.length > 0
      ? mindScoresArr.reduce((a, b) => a + b, 0) / mindScoresArr.length
      : 0;

    // Calculate trend (compare first half to second half)
    const midpoint = Math.floor(moodScores.length / 2);
    const firstHalf = moodScores.slice(0, midpoint);
    const secondHalf = moodScores.slice(midpoint);
    
    const avgFirst = firstHalf.length > 0 
      ? firstHalf.reduce((a, b) => a + b, 0) / firstHalf.length 
      : 0;
    const avgSecond = secondHalf.length > 0 
      ? secondHalf.reduce((a, b) => a + b, 0) / secondHalf.length 
      : 0;
    
    const moodTrend = avgSecond - avgFirst;
    const moodTrendPercent = avgFirst > 0 ? ((avgSecond - avgFirst) / avgFirst) * 100 : 0;

    // Engagement rate
    const daysInRange = parseInt(timeRange);
    const engagementRate = (checkins.length / daysInRange) * 100;

    // Sleep stats
    const avgSleep = sleepData.length > 0
      ? sleepData.reduce((a, b) => a + (b.duration_min || 0), 0) / sleepData.length / 60
      : 0;

    const avgSleepQuality = sleepData.length > 0
      ? sleepData.reduce((a, b) => a + (b.quality_self_rating || 0), 0) / sleepData.length
      : 0;

    return {
      avgMood: avgMood.toFixed(1),
      avgBody: avgBody.toFixed(1),
      avgMind: avgMind.toFixed(1),
      moodTrend,
      moodTrendPercent: moodTrendPercent.toFixed(1),
      engagementRate: engagementRate.toFixed(0),
      checkinsCount: checkins.length,
      avgSleep: avgSleep.toFixed(1),
      avgSleepQuality: avgSleepQuality.toFixed(1),
      goalsCompleted: goals.filter(g => g.status === 'completed').length,
      goalsActive: goals.filter(g => g.status === 'active').length,
      crisisCount: crisisEvents.length,
      journalCount: journalEntries.length
    };
  }, [checkins, sleepData, goals, crisisEvents, journalEntries, timeRange]);

  // Prepare chart data
  const moodChartData = useMemo(() => {
    return checkins.map(c => ({
      date: format(parseISO(c.date), 'MMM d'),
      mood: emotionScores[c.emotion] || 5,
      body: bodyScores[c.body_feeling] || 5,
      mind: mindScores[c.mind_state] || 5,
      emotion: c.emotion
    }));
  }, [checkins]);

  const sleepChartData = useMemo(() => {
    return sleepData.map(s => ({
      date: format(parseISO(s.date), 'MMM d'),
      hours: (s.duration_min || 0) / 60,
      quality: s.quality_self_rating || 0
    }));
  }, [sleepData]);

  // Emotion frequency analysis
  const emotionFrequency = useMemo(() => {
    const freq: Record<string, number> = {};
    checkins.forEach(c => {
      if (c.emotion) {
        freq[c.emotion] = (freq[c.emotion] || 0) + 1;
      }
    });
    return Object.entries(freq)
      .map(([emotion, count]) => ({ emotion, count, percent: ((count / checkins.length) * 100).toFixed(0) }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  }, [checkins]);

  const getTrendIcon = (trend: number) => {
    if (trend > 0.5) return <TrendingUp className="h-4 w-4 text-green-500" />;
    if (trend < -0.5) return <TrendingDown className="h-4 w-4 text-red-500" />;
    return <Minus className="h-4 w-4 text-muted-foreground" />;
  };

  const getMoodLabel = (score: number) => {
    if (score >= 8) return { label: 'Excellent', color: 'text-green-500' };
    if (score >= 6) return { label: 'Good', color: 'text-emerald-500' };
    if (score >= 4) return { label: 'Fair', color: 'text-yellow-500' };
    if (score >= 2) return { label: 'Low', color: 'text-orange-500' };
    return { label: 'Struggling', color: 'text-red-500' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!consent) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Info className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold mb-2">No Data Sharing Consent</h3>
          <p className="text-muted-foreground max-w-md mx-auto">
            {clientName} hasn't shared their Therappi data with you yet. 
            They can enable data sharing from their Client Portal.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">{clientName}'s Progress</h2>
          <p className="text-muted-foreground">Clinical insights and outcome tracking</p>
        </div>
        <Select value={timeRange} onValueChange={setTimeRange}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="14">Last 14 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="60">Last 60 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Alert for crisis events */}
      {crisisEvents.length > 0 && (
        <Card className="border-red-500/50 bg-red-500/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-red-500 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Crisis Events Recorded ({crisisEvents.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {crisisEvents.slice(0, 3).map(event => (
                <div key={event.id} className="flex items-center justify-between p-2 bg-background rounded">
                  <div>
                    <p className="font-medium">{event.trigger || 'Crisis event'}</p>
                    <p className="text-xs text-muted-foreground">
                      {format(parseISO(event.timestamp), 'PPp')} · Severity: {event.severity_score}/10
                    </p>
                  </div>
                  <Badge variant="outline">{event.action_taken || 'Used coping tools'}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Key Metrics */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Heart className="h-4 w-4 text-rose-500" />
                Emotional Wellbeing
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-baseline gap-2">
                <span className={`text-2xl font-bold ${getMoodLabel(parseFloat(stats.avgMood)).color}`}>
                  {stats.avgMood}/10
                </span>
                {getTrendIcon(stats.moodTrend)}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {getMoodLabel(parseFloat(stats.avgMood)).label} · 
                {parseFloat(stats.moodTrendPercent) > 0 ? '+' : ''}{stats.moodTrendPercent}% trend
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Activity className="h-4 w-4 text-emerald-500" />
                Physical State
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-baseline gap-2">
                <span className={`text-2xl font-bold ${getMoodLabel(parseFloat(stats.avgBody)).color}`}>
                  {stats.avgBody}/10
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Body awareness score
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Brain className="h-4 w-4 text-blue-500" />
                Mental Clarity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-baseline gap-2">
                <span className={`text-2xl font-bold ${getMoodLabel(parseFloat(stats.avgMind)).color}`}>
                  {stats.avgMind}/10
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Cognitive state average
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Calendar className="h-4 w-4 text-purple-500" />
                Engagement
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold">{stats.engagementRate}%</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {stats.checkinsCount} check-ins recorded
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Charts */}
      <Tabs defaultValue="mood" className="space-y-4">
        <TabsList>
          <TabsTrigger value="mood" className="gap-2">
            <Heart className="h-4 w-4" />
            Mood Trends
          </TabsTrigger>
          <TabsTrigger value="sleep" className="gap-2">
            <Moon className="h-4 w-4" />
            Sleep
          </TabsTrigger>
          <TabsTrigger value="patterns" className="gap-2">
            <BarChart3 className="h-4 w-4" />
            Patterns
          </TabsTrigger>
          <TabsTrigger value="goals" className="gap-2">
            <Target className="h-4 w-4" />
            Goals
          </TabsTrigger>
          <TabsTrigger value="balance" className="gap-2">
            <Scale className="h-4 w-4" />
            Balance
          </TabsTrigger>
        </TabsList>

        <TabsContent value="mood">
          <Card>
            <CardHeader>
              <CardTitle>Emotional & Mental Wellbeing Over Time</CardTitle>
              <CardDescription>
                Tracking mood, body awareness, and mental clarity scores
              </CardDescription>
            </CardHeader>
            <CardContent>
              {moodChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={moodChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <YAxis domain={[0, 10]} stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Area type="monotone" dataKey="mood" stroke="hsl(var(--primary))" fill="hsl(var(--primary) / 0.2)" name="Mood" />
                    <Area type="monotone" dataKey="body" stroke="hsl(var(--accent))" fill="hsl(var(--accent) / 0.2)" name="Body" />
                    <Area type="monotone" dataKey="mind" stroke="hsl(var(--secondary))" fill="hsl(var(--secondary) / 0.2)" name="Mind" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                  No check-in data available for this period
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sleep">
          <Card>
            <CardHeader>
              <CardTitle>Sleep Patterns</CardTitle>
              <CardDescription>
                Duration and quality of sleep over time
              </CardDescription>
            </CardHeader>
            <CardContent>
              {sleepChartData.length > 0 ? (
                <>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="p-4 bg-muted/30 rounded-lg">
                      <p className="text-sm text-muted-foreground">Average Duration</p>
                      <p className="text-2xl font-bold">{stats?.avgSleep} hrs</p>
                    </div>
                    <div className="p-4 bg-muted/30 rounded-lg">
                      <p className="text-sm text-muted-foreground">Average Quality</p>
                      <p className="text-2xl font-bold">{stats?.avgSleepQuality}/10</p>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={sleepChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                      <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                      />
                      <Bar dataKey="hours" fill="hsl(var(--primary))" name="Hours" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                  No sleep data available for this period
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="patterns">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Most Frequent Emotions</CardTitle>
                <CardDescription>
                  Distribution of emotional states
                </CardDescription>
              </CardHeader>
              <CardContent>
                {emotionFrequency.length > 0 ? (
                  <div className="space-y-3">
                    {emotionFrequency.map((item, i) => (
                      <div key={item.emotion} className="flex items-center gap-3">
                        <span className="w-24 text-sm truncate">{item.emotion}</span>
                        <div className="flex-1 h-4 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-primary rounded-full transition-all"
                            style={{ width: `${item.percent}%` }}
                          />
                        </div>
                        <span className="text-sm text-muted-foreground w-12 text-right">
                          {item.percent}%
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">No emotion data</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Clinical Observations</CardTitle>
                <CardDescription>
                  Key patterns to discuss in session
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {stats && parseFloat(stats.moodTrendPercent) > 10 && (
                    <div className="flex items-start gap-2 p-3 bg-green-500/10 rounded-lg">
                      <ArrowUpRight className="h-4 w-4 text-green-500 mt-0.5" />
                      <div>
                        <p className="font-medium text-green-500">Positive Progress</p>
                        <p className="text-sm text-muted-foreground">
                          Mood scores show {stats.moodTrendPercent}% improvement
                        </p>
                      </div>
                    </div>
                  )}
                  {stats && parseFloat(stats.moodTrendPercent) < -10 && (
                    <div className="flex items-start gap-2 p-3 bg-orange-500/10 rounded-lg">
                      <ArrowDownRight className="h-4 w-4 text-orange-500 mt-0.5" />
                      <div>
                        <p className="font-medium text-orange-500">Declining Trend</p>
                        <p className="text-sm text-muted-foreground">
                          Mood scores show {Math.abs(parseFloat(stats.moodTrendPercent))}% decline - explore in session
                        </p>
                      </div>
                    </div>
                  )}
                  {stats && parseFloat(stats.avgSleep) < 6 && (
                    <div className="flex items-start gap-2 p-3 bg-yellow-500/10 rounded-lg">
                      <Moon className="h-4 w-4 text-yellow-500 mt-0.5" />
                      <div>
                        <p className="font-medium text-yellow-500">Sleep Concern</p>
                        <p className="text-sm text-muted-foreground">
                          Average sleep of {stats.avgSleep}hrs may be impacting wellbeing
                        </p>
                      </div>
                    </div>
                  )}
                  {stats && parseFloat(stats.engagementRate) > 80 && (
                    <div className="flex items-start gap-2 p-3 bg-blue-500/10 rounded-lg">
                      <CheckCircle2 className="h-4 w-4 text-blue-500 mt-0.5" />
                      <div>
                        <p className="font-medium text-blue-500">High Engagement</p>
                        <p className="text-sm text-muted-foreground">
                          Client is actively using the app ({stats.engagementRate}% check-in rate)
                        </p>
                      </div>
                    </div>
                  )}
                  {crisisEvents.length > 2 && (
                    <div className="flex items-start gap-2 p-3 bg-red-500/10 rounded-lg">
                      <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5" />
                      <div>
                        <p className="font-medium text-red-500">Multiple Crisis Events</p>
                        <p className="text-sm text-muted-foreground">
                          {crisisEvents.length} crisis events recorded - review triggers and coping
                        </p>
                      </div>
                    </div>
                  )}
                  {(!stats || checkins.length < 5) && (
                    <div className="flex items-start gap-2 p-3 bg-muted/50 rounded-lg">
                      <Info className="h-4 w-4 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="font-medium">Insufficient Data</p>
                        <p className="text-sm text-muted-foreground">
                          Need more check-ins for reliable pattern analysis
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="goals">
          <Card>
            <CardHeader>
              <CardTitle>Treatment Goals Progress</CardTitle>
              <CardDescription>
                Client's self-set goals and completion status
              </CardDescription>
            </CardHeader>
            <CardContent>
              {goals.length > 0 ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="p-4 bg-green-500/10 rounded-lg text-center">
                      <p className="text-2xl font-bold text-green-500">{stats?.goalsCompleted}</p>
                      <p className="text-sm text-muted-foreground">Completed</p>
                    </div>
                    <div className="p-4 bg-blue-500/10 rounded-lg text-center">
                      <p className="text-2xl font-bold text-blue-500">{stats?.goalsActive}</p>
                      <p className="text-sm text-muted-foreground">Active</p>
                    </div>
                    <div className="p-4 bg-muted/30 rounded-lg text-center">
                      <p className="text-2xl font-bold">{goals.length}</p>
                      <p className="text-sm text-muted-foreground">Total</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {goals.slice(0, 10).map(goal => (
                      <div key={goal.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Target className={`h-4 w-4 ${goal.status === 'completed' ? 'text-green-500' : 'text-muted-foreground'}`} />
                          <div>
                            <p className="font-medium">{goal.title}</p>
                            <p className="text-xs text-muted-foreground">{goal.category}</p>
                          </div>
                        </div>
                        <Badge variant={goal.status === 'completed' ? 'default' : 'outline'}>
                          {goal.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="py-12 text-center text-muted-foreground">
                  <Target className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No goals recorded yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="balance">
          <ClientBalanceView 
            checkins={checkins}
            clientName={clientName}
            timeRange={timeRange}
          />
        </TabsContent>
      </Tabs>

      {/* Journal Summary */}
      {journalEntries.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Recent Journal Entries
            </CardTitle>
            <CardDescription>
              {stats?.journalCount} entries in the selected period
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {journalEntries.slice(0, 5).map(entry => (
                <div key={entry.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <div>
                    <p className="font-medium">{entry.title || 'Untitled Entry'}</p>
                    <p className="text-xs text-muted-foreground">
                      {format(parseISO(entry.created_at), 'PPp')}
                    </p>
                  </div>
                  {entry.mood && (
                    <Badge variant="outline">{entry.mood}</Badge>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
